import { RTLObserver } from "@ingka/webc-tools";
import SkapaElement from "@ingka/skapa-webc-element";
import {
  chevronRight,
  chevronLeft,
  chevronRightSmall,
  chevronLeftSmall
} from "@ingka/icon-store";
import { IconButton } from "@ingka/button-webc/passive.js";
import { Icon } from "@ingka/icon-webc/passive.js";
const styles = ':host{--skapa-carousel-control-offset:-1.25rem;display:none;height:2.5rem;pointer-events:none;position:relative;width:100%}:host([hidden]){visibility:hidden}.next,.prev{pointer-events:all;position:absolute;transition:opacity .2s cubic-bezier(.4,0,.4,1)}.next.hidden,.prev.hidden{opacity:0;pointer-events:none}.prev{left:var(--skapa-carousel-control-offset)}.next{right:var(--skapa-carousel-control-offset)}:host([overflow]){--skapa-carousel-control-offset:0.5rem;display:block}@media (min-width:37.5em){:host{display:block}}';
function generateTemplate({ html }) {
  const template = document.createElement("template");
  template.innerHTML = html`
    <style>
      ${styles}
    </style>
    <skapa-icon-button
      class="prev hidden"
      part="control-button"
      size="small"
      decorative-only
    >
      <skapa-icon slot="icon" icon="chevron-left"></skapa-icon>
    </skapa-icon-button>
    <skapa-icon-button
      class="next hidden"
      part="control-button"
      size="small"
      decorative-only
    >
      <skapa-icon slot="icon" icon="chevron-right"></skapa-icon>
    </skapa-icon-button>
  `;
  return { main: template };
}
const NavigationEventDirection = {
  PREV: "CAROUSEL_CONTROL_PREV",
  NEXT: "CAROUSEL_CONTROL_NEXT"
};
const NavigationEventType = "SKAPA_CAROUSEL_NAVIGATION";
class NavigationEvent extends Event {
  constructor(navigationType) {
    super(NavigationEventType, { composed: true, bubbles: true });
    this.navigationType = navigationType;
  }
}
const UDPATE_FREQUENCY = 1 / 10;
class CarouselControls extends SkapaElement {
  constructor() {
    super();
    this.state = {
      visibility: {
        prevButton: false,
        nextButton: false
      }
    };
    this.handlePointerUp = (ev) => {
      const { PREV, NEXT } = NavigationEventDirection;
      this.dispatchEvent(
        new NavigationEvent(ev.currentTarget === this.prevButton ? PREV : NEXT)
      );
    };
    this.syncState = () => {
      const {
        prevButton,
        nextButton,
        state: { visibility }
      } = this;
      prevButton.classList.toggle("hidden", !visibility.prevButton);
      prevButton.disabled = !visibility.prevButton;
      nextButton.classList.toggle("hidden", !visibility.nextButton);
      nextButton.disabled = !visibility.nextButton;
    };
    this.overflowInconsistentControlsForTouchUsers = (ev) => {
      if (!ev.composedPath().includes(this)) this.hidden = true;
    };
    const shadow = this.attachShadow({ mode: "open" });
    const template = this.getTemplates(generateTemplate);
    shadow.append(template.main.content.cloneNode(true));
    this.leftButton = shadow.querySelector(".prev");
    this.rightButton = shadow.querySelector(".next");
    this.iconPrev = this.leftButton.querySelector("[icon]");
    this.iconNext = this.rightButton.querySelector("[icon]");
  }
  static get observedAttributes() {
    return ["overflow", "skapa-rtl"];
  }
  connectedCallback() {
    RTLObserver.addTarget(this);
    this.leftButton.addEventListener("click", this.handlePointerUp);
    this.rightButton.addEventListener("click", this.handlePointerUp);
    const parent = this.parentNode && this.parentNode.host;
    if (parent)
      parent.addEventListener(
        "touchstart",
        this.overflowInconsistentControlsForTouchUsers,
        { once: true, passive: true }
      );
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    RTLObserver.removeTarget(this);
    this.leftButton.removeEventListener("click", this.handlePointerUp);
    this.rightButton.removeEventListener("click", this.handlePointerUp);
    const parent = this.parentNode && this.parentNode.host;
    if (parent)
      parent.removeEventListener(
        "touchstart",
        this.overflowInconsistentControlsForTouchUsers
      );
  }
  get isRTL() {
    return this.hasAttribute("skapa-rtl");
  }
  get nextButton() {
    return this.isRTL ? this.leftButton : this.rightButton;
  }
  get prevButton() {
    return this.isRTL ? this.rightButton : this.leftButton;
  }
  set prev(value) {
    this.state.visibility.prevButton = value;
    this.micro(
      this.syncState,
      "synchronising control buttons visibility state",
      UDPATE_FREQUENCY
    );
  }
  set next(value) {
    this.state.visibility.nextButton = value;
    this.micro(
      this.syncState,
      "synchronising control buttons visibility state",
      UDPATE_FREQUENCY
    );
  }
  // This should be refactored into slot provisions instead.
  get overflow() {
    return this.hasAttribute("overflow");
  }
  set overflow(value) {
    this.toggleAttribute("overflow", !!value);
  }
  // Returns an offset value to ensure the paging buttons do not cover the content.
  get controllerWidth() {
    return this.overflow ? 40 : 0;
  }
  attributeChangedCallback() {
    if (this.overflow) {
      this.leftButton.size = this.rightButton.size = "xsmall";
      this.leftButton.inverse = this.rightButton.inverse = true;
      this.leftButton.staticColour = this.rightButton.staticColour = true;
      this.iconPrev.icon = "chevron-left-small";
      this.iconNext.icon = "chevron-right-small";
    } else {
      this.leftButton.size = this.rightButton.size = "small";
      this.leftButton.inverse = this.rightButton.inverse = false;
      this.leftButton.staticColour = this.rightButton.staticColour = false;
      this.iconPrev.icon = "chevron-left";
      this.iconNext.icon = "chevron-right";
    }
    this.micro(
      this.syncState,
      "synchronising control buttons visibility state",
      UDPATE_FREQUENCY
    );
  }
}
CarouselControls.skapaTagName = "carousel-controls";
CarouselControls.skapaDependencies = [IconButton, Icon];
CarouselControls.skapaIconDependencies = [
  chevronRight,
  chevronLeft,
  chevronRightSmall,
  chevronLeftSmall
];
export {
  CarouselControls,
  NavigationEvent,
  NavigationEventDirection,
  NavigationEventType
};
